import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { openSubscribe } from "./SubscribeModalHost";
import { useIsSubscribed } from "../lib/useIsSubscribed";
import { t as tRaw } from "../lib/i18n";
import { DigestLaunchNotice } from "./DigestLaunchNotice";

// FloatingSubscribeCTA is mounted at the app root (outside any per-page
// LangContext.Provider), which is why an inline `useT()` call previously
// always returned the createContext("en") default — resulting in an English
// button regardless of the visitor's selected language. We source the lang
// ourselves from `localStorage.ir_lang` (the same key HomePage writes to)
// and re-read it on the `ir_lang_changed` window event that HomePage now
// dispatches on every toggle, keeping the CTA in sync same-tab and cross-tab.
//
// PA-hub override (2026-02-23): on /papiamento the localStorage-driven
// EN/NL/ES value is the wrong signal — PA readers expect the CTA in
// Papiamento (Aruba tab → pap_aw) or Papiamentu (Curaçao/Bonaire/All-Islands
// tabs → pap). PapiamentoPage broadcasts its current variant via
// sessionStorage `ir_pa_variant` + the `ir_pa_variant_changed` event; when
// we're on /papiamento we prefer that over `ir_lang`. Default to pap_aw
// because Aruba is the primary market and the PA page defaults to
// "All Islands" → pap_aw.
const LANG_KEY = "ir_lang";
const PA_VARIANT_KEY = "ir_pa_variant";
const readSiteLang = () => {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === "en" || v === "es" || v === "nl" || v === "pap") return v;
  } catch (_) { /* ignore */ }
  return "en";
};
const readPaVariant = () => {
  try {
    const v = sessionStorage.getItem(PA_VARIANT_KEY);
    if (v === "pap_aw" || v === "pap") return v;
  } catch (_) { /* ignore */ }
  return "pap_aw";
};

// Global floating CTA that appears after the user has scrolled past the hero.
// - Hides on /admin (owner UI shouldn't get spammed)
// - Hides after user dismisses it during a session (sessionStorage)
// - Hides permanently once the visitor is already subscribed on this device
// - Opens the shared 5-step SubscribeModal via openSubscribe()
export const FloatingSubscribeCTA = () => {
  const location = useLocation();
  const isPaHub = location.pathname.startsWith("/papiamento");
  const [siteLang, setSiteLang] = useState(readSiteLang);
  const [paVariant, setPaVariant] = useState(readPaVariant);
  const lang = isPaHub ? paVariant : siteLang;
  const isSubscribed = useIsSubscribed();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem("wa_cta_dismissed") === "1");
    } catch (_) {
      // sessionStorage may be unavailable in some private-browsing modes
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // Site-wide EN/NL/ES toggle → refresh siteLang. PA-hub island tab
    // change → refresh paVariant (fired by PapiamentoPage's useEffect).
    const refreshSite = () => setSiteLang(readSiteLang());
    const refreshPa = () => setPaVariant(readPaVariant());
    const onStorage = (e) => {
      if (e.key === LANG_KEY) refreshSite();
    };
    window.addEventListener("ir_lang_changed", refreshSite);
    window.addEventListener("ir_pa_variant_changed", refreshPa);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("ir_lang_changed", refreshSite);
      window.removeEventListener("ir_pa_variant_changed", refreshPa);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Re-read paVariant on every /papiamento entry — covers the case where
  // the CTA mounts before PapiamentoPage's initial useEffect fires.
  useEffect(() => {
    if (isPaHub) setPaVariant(readPaVariant());
  }, [isPaHub]);

  const isAdmin = location.pathname.startsWith("/admin");
  if (isAdmin || dismissed || !visible || isSubscribed) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem("wa_cta_dismissed", "1"); } catch (_) {
      // sessionStorage may be unavailable in some private-browsing modes
    }
    setDismissed(true);
  };

  return (
    <div
      // z-[60] so the widget always stacks above the sticky Header
      // (z-50) — protects against the "top of widget renders behind
      // the nav bar" case on short viewports and compressed-header
      // scroll states.
      //
      // `hidden sm:flex` — on mobile the MobileStickyBar already
      // covers this CTA slot (bottom-anchored, z-90). Rendering both
      // caused the FloatingCTA's Get-Daily-Digest button to disappear
      // behind the sticky bar. Desktop is untouched.
      //
      // `sm:bottom-24` gives a bit more clearance from the viewport
      // bottom so the launch-notice pill above the button never
      // brushes anything else while scrolling.
      className="fixed z-[60] hidden sm:flex sm:bottom-24 sm:right-6 animate-fade-in-up flex-col items-end gap-2"
      data-testid="floating-cta"
    >
      <DigestLaunchNotice
        variant="short"
        lang={lang}
        className="max-w-[240px] shadow-md text-[11px] py-1.5"
        testid="floating-cta-launch-notice"
      />
      <div className="relative">
        <button
          type="button"
          onClick={() => openSubscribe("floating_cta")}
          className="flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-[#E63946] hover:bg-[#cf2f3c] text-white text-sm sm:text-[15px] font-semibold shadow-xl hover:shadow-2xl transition-all whitespace-nowrap"
          data-testid="floating-cta-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="shrink-0" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
          </svg>
          <span>{tRaw(lang, "get_daily_digest")}</span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-[#6B7280] hover:text-[#1A1A1A] shadow-md flex items-center justify-center border border-[#E5E5E5]"
          data-testid="floating-cta-dismiss"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
};
